import { Breadcrumb, Breadcrumbs, PrimaryButton, SecondaryAlert, TertiaryButton } from 'kagr-ui';
import * as React from 'react';
import Paths from '../../models/enums/paths';
import * as styles from './create-segment.module.scss';
import * as breadcrumbStyles from './breadcrumbs/segment-breadcrumbs.module.scss';
import { useHistory, useLocation } from 'react-router';
import { useCreateSegment } from '../../hooks/useCreateSegment';
import { CollapsibleSection } from '../common/collapsible-section';
import EditSegmentDetails from './build-segment/edit-segment-details';
import ExportSegment from './export-segment/export-segment';
import {
  BuildExcludeSelectedCompositionContext,
  BuildIncludeSelectedCompositionContext,
  BuildSegmentContext,
  BuildSegmentExcludedContext,
  buildSegmentReducer
} from './build-segment/useBuildSegment';
import { SegmentAttributeOperator, SegmentCompositionSelection } from '../../models/segments';
import SegmentResultsBuild from './segment-results/segment-results-build';
import BuildSegment from './build-segment/build-segment';
import { useSegment } from '../../hooks/useSegment';
import { datadogRum } from '@datadog/browser-rum';
import useUser from '../../hooks/useUser';

const breadcrumbs: Breadcrumb[] = [
  {
    title: 'Segments',
    path: Paths.Segments
  }
];

/**
 * Create New Segment view
 * Interface rendered to create a new segment from scratch
 */
const CreateSegment = () => {
  const history = useHistory();
  const { search } = useLocation();
  const clonedId = new URLSearchParams(search);
  const { segmentQuery } = useSegment(clonedId.get('clonedId'));
  const segmentMutation = useCreateSegment();
  const [detailsCollapsed, setDetailsCollapsed] = React.useState<boolean>(false);
  const [buildCollapsed, setBuildCollapsed] = React.useState<boolean>(false);
  const [name, setName] = React.useState<string>('');
  const [description, setDescription] = React.useState<string>('');
  const [shared, setShared] = React.useState<string>('Do not share');
  const [includedAttributes, dispatch] = React.useReducer(buildSegmentReducer, {
    operator: SegmentAttributeOperator.And,
    predicates: []
  });
  const [excludedAttributes, excludedDispatch] = React.useReducer(buildSegmentReducer, {
    operator: SegmentAttributeOperator.And,
    predicates: []
  });
  const [includeSegmentComposition, setIncludeSegmentComposition] = React.useState<SegmentCompositionSelection>({
    segmentId: null,
    segmentOperator: null,
    customers: null,
    segmentName: ''
  });
  const [excludeSegmentComposition, setExcludeSegmentComposition] = React.useState<SegmentCompositionSelection>({
    segmentId: null,
    segmentOperator: null,
    customers: null,
    segmentName: ''
  });

  const clonedSegment = React.useRef<boolean>(!!clonedId?.get('clonedId'));
  const { getCurrentUser } = useUser();
  const userName = React.useRef<string>('');

  React.useEffect(() => {
    void checkUser();
  }, []);

  const checkUser = async () => {
    try {
      const currentUser = await getCurrentUser();
      userName.current = `${currentUser.firstName} ${currentUser.lastName}`;
    } catch {
      userName.current = '';
    }
  };

  React.useEffect(() => {
    if (segmentQuery.data) {
      setName(`Copy of ${segmentQuery.data.name}`);
      setDescription(segmentQuery.data.description);
      setShared(segmentQuery.data.isShared ? 'Share' : 'Do not share');
      dispatch({ type: 'setAttributes', attributes: segmentQuery.data.selectedAttributes.include });
      excludedDispatch({ type: 'setAttributes', attributes: segmentQuery.data.selectedAttributes.exclude });
      if (segmentQuery.data.composition?.include) {
        setIncludeSegmentComposition({
          segmentId: segmentQuery.data.composition.include.segmentId,
          segmentOperator: segmentQuery.data.composition.include.segmentOperator,
          customers: segmentQuery.data.composition.include.customers,
          segmentName: segmentQuery.data.composition.include.segmentName
        });
      }
      if (segmentQuery.data.composition?.exclude) {
        setExcludeSegmentComposition({
          segmentId: segmentQuery.data.composition.exclude.segmentId,
          segmentOperator: segmentQuery.data.composition.exclude.segmentOperator,
          customers: segmentQuery.data.composition.exclude.customers,
          segmentName: segmentQuery.data.composition.exclude.segmentName
        });
      }
      clonedSegment.current = true;
    }
  }, [segmentQuery.data]);

  function createSegment() {
    let selectedComposition = {};

    if (includeSegmentComposition.segmentId) {
      selectedComposition = { include: includeSegmentComposition };
    }
    if (excludeSegmentComposition.segmentId) {
      selectedComposition = { ...selectedComposition, exclude: excludeSegmentComposition };
    }

    segmentMutation.mutate(
      {
        name,
        description,
        isShared: shared == 'Share',
        attributes: {
          include: includedAttributes,
          exclude: excludedAttributes
        },
        composition: {
          ...selectedComposition
        }
      },
      {
        onSuccess: () => {
          includedAttributes.predicates.forEach((att) => {
            logCreatedAttributes(att);
          });
          excludedAttributes.predicates.forEach((att) => {
            logCreatedAttributes(att);
          });
          history.push(Paths.Segments);
        }
      }
    );
  }

  const logCreatedAttributes = (included: any) => {
    if ('predicates' in included) {
      included.predicates.forEach((att) => {
        logCreatedAttributes(att);
      });
    } else {
      datadogRum.addUserAction('create-segment', {
        user_action_details: {
          segment_id: 'new segment',
          segment_name: name,
          is_segment_shared: shared,
          user_name: userName.current,
          attribute: included.name
        }
      });
    }
  };

  const buildIncludeSelectedCompositionObj = React.useMemo(
    () => ({
      selectedComoposition: includeSegmentComposition,
      changeSelectedComposition: setIncludeSegmentComposition
    }),
    [includeSegmentComposition, setIncludeSegmentComposition]
  );

  const buildExcludeSelectedCompositionObj = React.useMemo(
    () => ({
      selectedComoposition: excludeSegmentComposition,
      changeSelectedComposition: setExcludeSegmentComposition
    }),
    [excludeSegmentComposition, setExcludeSegmentComposition]
  );

  return (
    <div className={styles.root}>
      <Breadcrumbs
        breadcrumbs={breadcrumbs}
        currentPage={<h1>Create New Segment</h1>}
        className={breadcrumbStyles.breadcrumb}
      />
      <div className={styles.error}>
        {segmentMutation.isError && <SecondaryAlert type="error" message={segmentMutation.error.message} />}
      </div>
      <CollapsibleSection title="Enter Segment Details" collapsed={detailsCollapsed} onCollapse={setDetailsCollapsed}>
        <EditSegmentDetails
          name={name}
          description={description}
          shared={shared}
          onNameChange={setName}
          onDescriptionChange={setDescription}
          onSharedChange={setShared}
          isClonedSegment={clonedSegment.current}
        />
      </CollapsibleSection>
      <CollapsibleSection
        title="Summary"
        collapsed={buildCollapsed}
        onCollapse={setBuildCollapsed}
        className={styles.buildSection}
        secondaryAction={
          <ExportSegment
            name={name}
            includedAttributes={includedAttributes}
            excludedAttributes={excludedAttributes}
            segmentId={''}
            segmentShared={shared}
            userName={userName.current}
          />
        }
      >
        <React.Fragment>
          <BuildSegmentContext.Provider value={{ selectedAttributes: includedAttributes, dispatch: dispatch }}>
            <BuildSegmentExcludedContext.Provider
              value={{ selectedAttributes: excludedAttributes, dispatch: excludedDispatch }}
            >
              <BuildIncludeSelectedCompositionContext.Provider value={buildIncludeSelectedCompositionObj}>
                <BuildExcludeSelectedCompositionContext.Provider value={buildExcludeSelectedCompositionObj}>
                  <SegmentResultsBuild />
                  <BuildSegment isNew />
                  <BuildSegment isNew exclude />
                </BuildExcludeSelectedCompositionContext.Provider>
              </BuildIncludeSelectedCompositionContext.Provider>
            </BuildSegmentExcludedContext.Provider>
          </BuildSegmentContext.Provider>
        </React.Fragment>
      </CollapsibleSection>
      <div className={styles.actionFooter}>
        <TertiaryButton
          component="button"
          text="Cancel"
          title="Cancel"
          handleClick={() => {
            history.push(Paths.Segments);
          }}
        />
        <PrimaryButton
          text="Save Segment"
          title="Save Segment"
          handleClick={createSegment}
          deactive={
            name.length === 0 ||
            description.length === 0 ||
            (includedAttributes.predicates.length === 0 && excludedAttributes.predicates.length === 0) ||
            shared === ''
          }
        />
      </div>
    </div>
  );
};

export default CreateSegment;
